import { HostConfig } from './types'

import BigWinnerBackground from '@Assets/big-winner-background.png'
import DamagedTicketImage from '@Assets/damaged-ticket-image.svg'
import TicketScannerImage from '@Assets/scanner-image.svg'
import BarcodeDrawGame from '@Assets/customers/michigan/barcode-draw-game.svg'
import BarcodeInstantGame from '@Assets/customers/michigan/barcode-instant-game.svg'
import BarcodePullTabs from '@Assets/customers/michigan/barcode-pull-tabs.svg'
import ClaimFormSubmittedImage from '@Assets/claim-form-submitted.svg'

import { ClaimantStep } from '@Components/claim-form-flow/screens/claimant-step/claimant-step'
import { ContactStep } from '@Components/claim-form-flow/screens/contact-step/contact-step'
import { TicketsStep } from '@Components/claim-form-flow/screens/tickets-step/tickets-step'
import { SignStep } from '@Components/claim-form-flow/screens/sign-step/sign-step'

export const GAMBYT_DEMO_HOST_CONFIG: HostConfig = {
  standardFlowRangeInCents: {
    min: 60001,
    max: 9999999,
  },
  reasonForVisitScreen: {
    prizeAmountDisclaimer: (
      <p>
        Prizes up to $600 may be cashed at any Lottery retailer. Winners of $100,000 or more must
        claim their prize at the Lottery headquarters.
      </p>
    ),
  },
  confirmationScreen: {
    itemsNeeded: [
      'Winning tickets',
      'Valid government-issued photo identification',
      'Social Security card',
    ],
    disclaimer: (
      <p>
        *Note: The name on your photo identification and Social Security card must match, or we will
        be unable to process your claim. You can find a full list of acceptable proof of identity at{' '}
        <a target="_blank" href="https://www.gambyt.com">
          gambyt.com
        </a>
      </p>
    ),
    additionalSections: [<h1>What's Next?</h1>, <p>We look forward to seeing you!</p>],
    claimFormSections: (beginClaimForm) => [
      <h1>What's Next?</h1>,
      <p>
        To claim your prize,{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            beginClaimForm()
          }}>
          <strong>you must submit a claim form</strong>
        </a>
        . You can fill this out in the next step, and a link has been sent to you for later.
      </p>,
      <br />,
      <p>We look forward to seeing you!</p>,
    ],
  },
  calendarEvent: {
    details: `Remember to bring the following to your appointment: Winning Ticket, Non-expired government issued picture ID, Social Security Card, and filled out Ticket Receipt Form`,
    title: 'Gambyt Demo Lottery Claim Appointment',
  },
  bigWinnerFlow: {
    backgroundImage: BigWinnerBackground,
    details: [
      <p>Prizes of $100,000 or more must be claimed at Lottery headquarters.</p>,
      <br />,
      <p>
        Please call <a href="tel:555-555-5555">555-555-5555</a> to speak with a representative and
        make an appointment.
      </p>,
    ],
  },
  damagedTicketFlow: {
    backgroundImage: DamagedTicketImage,
    details: [
      <h1>What's Next?</h1>,
      <p>Since we’re unsure of the state of your ticket, we’ll need a little more information.</p>,
      <br />,
      <p>
        Please contact our 24/7 Customer Support Team by submitting this{' '}
        <a href="https://gambyt.com">form</a> or calling{' '}
        <a href="tel:1-555-555-5555">1-555-555-5555</a>.
      </p>,
    ],
  },
  manyTicketsFlow: {
    ticketThreshold: 15,
    backgroundImage: DamagedTicketImage,
    details: [
      <h1>What's Next?</h1>,
      <p>
        Please call <a href="tel:555-555-5555">555-555-5555</a> to speak with a representative and
        make an appointment.
      </p>,
    ],
  },
  unknownWinFlow: {
    backgroundImage: TicketScannerImage,
    details: [
      <h1>What's Next?</h1>,
      <p>
        Not sure how much you've won? Tap <a href="https://gambyt.com">here</a> to use our online
        ticket checker and learn the amount of your prize!
      </p>,
    ],
  },
  regularTicketFlow: {
    backgroundImage: TicketScannerImage,
    details: [
      <p>
        Prizes from $1-$600 can be cashed at any retailer. Please visit a retailer to redeem your
        ticket or call <a href="tel:555-555-5555">555-555-5555</a> to speak with a representative.
      </p>,
    ],
  },
  bookableScreen: {
    header: 'Select Date & Time',
    subHeader: 'When would you like to claim your prize? Please select a date and time.',
  },
  playerInformation: {
    ageForVerificationError:
      'Must be 18 or older to schedule an appointment online. If you are under 18, please call (###)-###-#### to schedule an appointment.',
    minAgeForPhoneVerification: 18,
    phoneConsentLabel: (
      <p>
        Online appointments require text messages to verify and edit appointments. I agree to
        receive text messages from Gambyt Demo Lottery. Message & data rates may apply. Message
        Frequency Varies. Text HELP for help. STOP to cancel.
      </p>
    ),
    agreeToTermsLabel: (
      <p>
        I have reviewed and agree to the <a href="https://gambyt.com">Terms of Use</a> and{' '}
        <a href="https://gambyt.com">Privacy Policy</a>.
      </p>
    ),
    emailConsentLabel: <p>Online appointments require an email to verify and edit appointments.</p>,
  },
  claimFormConfig: {
    steps: [
      { key: 'claimant', schemaKeys: ['claimant'], label: 'Claimant', component: ClaimantStep },
      { key: 'contact', schemaKeys: ['contact'], label: 'Contact', component: ContactStep },
      {
        key: 'tickets',
        schemaKeys: ['tickets', 'paymentMethod'],
        label: 'Ticket(s)',
        component: TicketsStep,
      },
      {
        key: 'signatureAndDate',
        schemaKeys: ['signatureAndDate'],
        label: 'Sign',
        component: SignStep,
      },
    ],
    legalText: (
      <>
        <p>
          <strong>Under penalties of perjury, I certify that:</strong>
        </p>
        <ol>
          <li>I am the true owner of the Lottery ticket being claimed.</li>
          <li>
            I am a US citizen or resident alien and the number shown on this form is my correct
            taxpayer identification number, OR I am a non-US citizen and I understand I am subject
            to 30 US Code Chapter 3 withholding unless an exception applies, and
          </li>
          <li>
            I am not subject to backup withholding because (a) I am exempt from backup withholding,
            or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject
            to backup withholding as a result of a failure to report all interest or dividends, or
            (c) the IRS has notified me that I am no longer subject to backup withholding.
          </li>
        </ol>
        <p>
          The Internal Revenue Service does not require your consent to any provision of this
          document other than the certifications required to avoid backup withholding.
        </p>
      </>
    ),
    gameTypeHelp: {
      title: 'Not sure?',
      content: (
        <>
          <h3>Draw Games</h3>
          <p>
            Draw-based games like Club Keno and Powerball. You can find the barcode number located
            beneath the barcode of the draw ticket, outlined in blue below. Also includes Fast Cash.
          </p>
          <img
            src={BarcodeDrawGame}
            alt="Draw Game barcode example"
            className="game-type-help-modal__barcode-image"
          />
          <h3>Instant Games</h3>
          <p>
            Also known as scratchers, these are instant win games you purchase at a retailer. You
            can find the barcode number located under the scratch area of the instant ticket,
            outlined in blue below. This number can be in two different formats.
          </p>
          <img
            src={BarcodeInstantGame}
            alt="Instant Game barcode example"
            className="game-type-help-modal__barcode-image"
          />
          <h3>Pull Tabs</h3>
          <p>
            You can find the barcode number underneath the barcode at the bottom of the ticket,
            outlined in blue below.
          </p>
          <img
            src={BarcodePullTabs}
            alt="Pull Tabs barcode example"
            className="game-type-help-modal__barcode-image"
          />
        </>
      ),
    },
    confirmation: {
      backgroundImage: '',
      heroImage: ClaimFormSubmittedImage,
      title: 'Submitted!',
      message: (
        <p>
          Your ticket receipt form has been successfully submitted. Please remember to bring all
          required documents with you to your claim appointment. We look forward to seeing you
          there!
        </p>
      ),
    },
  },
}
